import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CtaButton } from "@/components/CtaButton";
import { Reveal, Eyebrow } from "@/components/Reveal";

export const DreamLifeSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section ref={ref} data-testid="dreamlife-section" className="relative overflow-hidden">
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-20 sm:py-28 lg:grid-cols-12">
        <div className="order-2 lg:order-1 lg:col-span-6">
          <Reveal>
            <Eyebrow>I DON’T TEACH VISION BECAUSE I READ ABOUT IT</Eyebrow>
            <h2 className="mt-4 text-3xl font-extrabold leading-[1.06] tracking-tight text-cream sm:text-4xl lg:text-5xl">
              I BUILT A LIFE I ACTUALLY
              <br />
              WANTED TO <em className="font-serif font-medium italic text-lime">WAKE UP TO.</em>
            </h2>
          </Reveal>

          <Reveal className="mt-8 space-y-4 text-base text-cream/70 sm:text-lg" delay={0.1}>
            <p>
              Dasha Love is the author of <span className="text-cream">Paid For Being You</span>,{" "}
              <span className="text-cream">Stop F*cking Hiding</span>, and{" "}
              <span className="text-cream">Becoming Her Blueprint</span> — and the creator of Wild Woman
              Millions™.
            </p>
            <p>
              The books. The stages. The relationship. The bus parked on the beach. More than a thousand
              channeling videos and songs — recorded in one take and posted as they came. One clear vision
              at a time, practiced daily, until it became ordinary life.
            </p>
            <p className="font-serif text-xl italic text-cream sm:text-2xl">
              This isn’t about copying my life.
              <br />
              It’s about finally choosing yours.
            </p>
            <div className="pt-4">
              <CtaButton id="dreamlife" variant="ghost">CHOOSE MY VISION</CtaButton>
            </div>
          </Reveal>
        </div>

        <Reveal className="order-1 lg:order-2 lg:col-span-6" delay={0.15}>
          <motion.div style={{ y }} className="relative mx-auto max-w-md">
            <div className="absolute -inset-3 border border-lime/30" aria-hidden="true" />
            <div className="relative overflow-hidden border border-white/15 shadow-[0_50px_100px_rgba(0,0,0,0.7)]">
              <img
                src="/assets/dasha-dreamlife.jpg"
                alt="Dasha Love at the home she built around her vision"
                loading="lazy"
                className="w-full object-cover"
                data-testid="dreamlife-photo"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
              <p className="absolute bottom-4 left-1/2 w-full -translate-x-1/2 text-center font-mono text-[10px] tracking-[0.35em] text-cream/90">
                THE LIFE A CLEAR VISION BUILT
              </p>
            </div>
          </motion.div>
        </Reveal>
      </div>

      <Reveal className="mx-auto max-w-6xl px-5 pb-20 sm:pb-28" delay={0.1}>
        <p className="text-center font-mono text-[11px] tracking-[0.35em] text-lime">
          THE BOOKS. THE STAGES. THE LOVE. THE BUS. — ALL CHOSEN, ONE VISION AT A TIME.
        </p>
        <div data-testid="dreamlife-gallery" className="mt-8 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4 sm:grid sm:grid-cols-3 sm:overflow-visible sm:pb-0">
          {[
            { src: "/assets/life-clients.jpg", cap: "CLIENT WORK, ON THE BUS", alt: "Dasha working with clients inside the Love Bus" },
            { src: "/assets/life-family.jpg", cap: "MOTHERHOOD, WILDLY LIVED", alt: "Dasha with her child at play" },
            { src: "/assets/life-bus-interior.jpg", cap: "WE PARK THE BUS ON THE BEACH", alt: "Inside the Love Bus parked on the beach at sunset" },
          ].map((p, i) => (
            <figure key={p.cap} className={`group relative w-[78vw] shrink-0 snap-center overflow-hidden border border-white/10 sm:w-auto ${i === 1 ? "sm:-translate-y-4" : ""}`}>
              <img src={p.src} alt={p.alt} loading="lazy" data-testid={`dreamlife-gallery-${i + 1}`} className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/10 to-transparent" />
              <figcaption className="absolute bottom-4 left-0 w-full text-center font-mono text-[10px] tracking-[0.3em] text-cream">
                {p.cap}
              </figcaption>
            </figure>
          ))}
        </div>
      </Reveal>
      <Reveal className="border-t border-white/10 px-5 py-14 text-center" delay={0.1}>
        <p className="font-mono text-[11px] tracking-[0.35em] text-lime">PROOF OF PRACTICE</p>
        <p className="mx-auto mt-4 max-w-3xl font-serif text-2xl italic leading-snug text-cream sm:text-4xl" data-testid="channeling-strip">
          1,000+ channeling videos and songs — recorded in one take, posted as they came.
        </p>
        <p className="mt-4 font-mono text-[10px] tracking-[0.3em] text-cream/40">
          THE VISION ISN'T A HOBBY. IT'S A PRACTICE.
        </p>
      </Reveal>
    </section>
  );
};
